export interface IPoliciesResponse {
  list: IPolicy[];
  subject: ISubject;
}

export interface ISubjectRequest {
  policyId?: string;
  contractId?: string;
  taxCode?: string;
  vatCode?: string;
  businessName?: string;
  name?: string;
  surname?: string;
  birthDate?: string;
  limit?: number;
}

export interface ISubjectResponse {
  totalCount: number;
  subjects: ISubject[];
}

export enum EPolicyTypes {
  TRADIZIONALE = 'TRADIZIONALI',
  MULTIRAMO = 'MULTIRAMO',
  UNIT = 'UNIT',
  TCM = 'TCM',
  TCM_PPI = 'TCM/PPI',
  PREVIDENZIALE = 'PREVIDENZIALE',
  FONDO_PENSIONE = 'FONDO PENSIONE',
  PREVIDENZIALI = 'PREVIDENZIALI',
  LTC = 'LTC',
  PIP = 'PIP'
}

export type TPolicyTypes = keyof typeof EPolicyTypes;

export enum EPolicyStatus {
  ATTIVA = 'ATTIVA',
  ESTINTA = 'ESTINTA',
  TRASFERITA = 'TRASFERITA',
  IN_ATTESA = 'IN_ATTESA',
}

export type TPolicyStatus = keyof typeof EPolicyStatus;

export enum EPolicyCompany {
  ATHORA = '00056',
  ISLD = '00055',
  ISV = '00043',
  ISP = '00044'
}

export enum UserActivityScope {
  PAGATORE = 'pagatore',
  BENEFICIARIO_PREVIDENZIALE = 'beneficiario_previdenziale',
  DEFAULT = 'default',
}

export const ATHORA_COMPANY_IDS = [EPolicyCompany.ATHORA];

export const ISV_COMPANY_IDS = [EPolicyCompany.ISV, EPolicyCompany.ISLD, EPolicyCompany.ISP];

export enum ESubjectType {
  PF = 'PF',
  PG = 'PG',
}

export const IBIPS_PRODUCTS: EPolicyTypes[] = [
  EPolicyTypes.MULTIRAMO,
  EPolicyTypes.TRADIZIONALE,
  EPolicyTypes.UNIT,
];

export const NO_IBIPS_PRODUCTS: EPolicyTypes[] = [
  EPolicyTypes.TCM,
  EPolicyTypes.TCM_PPI,
  EPolicyTypes.LTC,
];

export interface IPolicy {
  policyId: string;
  companyId: string;
  companyPolicyId: string;
  bperContractNumber: string;
  abi: string;
  cab: string;
  agencyCode: string;
  agencyDescription: string;
  policyStatus: TPolicyStatus;
  productCode: string;
  productDescription: string;
  policyType: string;
  prizeType: string;
  emissionDate: string;
  effectDate: string;
  expiryDate: string;
}

export interface ISubject {
  ndg: string;
  name: string;
  surname: string;
  birthDate: string;
  taxCode: string;
  vatCode: string;
  businessName: string;
  type: ESubjectType;
}

export interface IPolicyDetailSubject {
  ndg?: string;
  name?: string;
  surname?: string;
  taxOrVatCode: string;
  birthDate?: string;
  type?: string;
  companyName?: string;
  fullAddress?: string;
  birthPlace?: string;
}

export interface IBeneficiary {
  codeChoice?: string;
  type?: string;
  name?: string;
  surname?: string;
  taxCode?: string;
  descBeneficiary?: string;
  severeDisability?: string;
  roles?: IBeneficiaryRole[];
}

export interface IBeneficiaryRole {
  role?: string;
  fiscalCode?: string;
  percentage?: string;
  relationshipWithSubscriber?: string;
  toBeDeleted?: string;
}

export interface IBeneficiaryObject extends IBeneficiary {
  toBeDeleted?: boolean;
  isNewBeneficiary?: boolean;
  quote?: string;
  isLegalSubject?: string;
  beneficiaryType?: string;
  specificBeneficiaryType?: string;

  // PF
  birthDate?: string;
  name?: string;
  surname?: string;
  taxCode?: string;
  citizenship?: string;
  sex?: string;
  birthPlace?: string;
  birthProvince?: string;
  birthCountry?: string;
  residenceCountry?: string;
  residencePlace?: string;
  residenceProvince?: string;
  residencePostalCode?: string;
  residenceToponym?: string;
  residenceAddress?: string;
  residenceAddressNumber?: string;

  // PG
  companyName?: string;
  vatCode?: string;
  shareCapital?: string;

  relationshipWithSubscriber?: string;
  iban?: string;
}

export interface IOperationFilter {
  operationCode: string;
  operationDesc: string;
  companyCode?: string;
}

export interface IPolicyOperation extends IOperationFilter {
  isPermitted?: boolean; // Remove if not needed
  notPermittedReason?: string; // Remove if not needed
}

export interface IPolicyDetail {
  isUserOperatingOFSWithoutCustomerOFS?: boolean;
  summary: {
    productDescription: string;
    productCode: string;
    policyType: string;
    policyStatus: string;
    policySubState: string;
    prizeType: string;
    emissionDate: string;
    expiryDate: string;
    effectDate: string;
    bperPolicyNumber: string;
    companyPolicyNumber: string;
    currentRisk?: string;
    currentRiskDescription?: string;
    lastPremiumPaymentDate?: string;
  };
  policyValue: {
    insuredAmount: string;
    lastValorizationDate: string;
    lastRevaluedCapital: string;
    totalPayments: string;
    insuredMaximum: string;
    lastVariationDate: string;
    lastPaymentDate: string;
    expiryDate: string;
    funds: IPolicyFunds[];
    paymentsList?: IPolicyValuePayment[];
    movementsList?: IMovement[];
    lastOperationDate?: string;
    codeRapporto?: string;
  };
  policyExtraInfo: {
    proposalNumber: string;
    companyProposalNumber: string;
    policyId: string;
    conventionCode: string;
    isin: string;
    paymentsState: string;
    branchOffice: string;
    agencyCode: string;
    contractNumber: string;
    ndg: string;
    fundCode: string;
    lineCode: string;
  };
  policyHolder: IPolicyDetailSubject;
  insured: IPolicyDetailSubject;
  legalSubjects: IPolicyDetailSubject[];
  beneficiaries: any[];
  permittedOperations: IPolicyOperation[];
  documents: IPolicyDocument[];
  intesaFurtherDetails?: IIntesaFurtherDetails;
}

export interface IIntesaFurtherDetails {
	uni20?: Uni20FurtherDetails;
	uni30?: Uni30FurtherDetails;
}

export interface Uni20FurtherDetails {
	policyHolder: PolicyPersonUni20;
	policybeneficiaries: PolicyBeneficiaryUni20[];
	lastOperationDate: string;
	paymentMode: string;
	nextPaymentDate: string;
	nextPaymentAmount: number;
	legalGuardian?: any // FIXME;
	legalSubject?: any // FIXME;
	ipotesiValoreRiscatto: any // FIXME;
}

export interface PolicyBeneficiaryUni20 {
    name: string;
    surname: string;
    fiscalCode: string;
    birthDate: string;
    placeOfBirth: string;
    countryOfBirth: string;
}

export interface Uni30FurtherDetails {
	summary: Uni30SummaryFurtherDetails;
	letters: Uni30Lettera[];
	policyValue: Uni30PolicyValueFurtherDetails;
	premiumPayment: Uni30PremiumPaymentFurtherDetails;
	garanzie: Uni30Garanzia[];
	indirizzamenti: Uni30Indirizzamento[];
  estemporanei: Uni30Estemporaneo[];
	titoli: Uni30Titoli;
	liquidazioni: Uni30Liquidazione[];
	quote: Uni30Quota[];
	trasferimenti: Uni30Trasferimento[];
	fiscalData: Uni30EntitaPolizzaDatiFiscali[];
  switchHistory: Uni30SwitchHistory[];
  policyHolder?: {
    idAnagrafico?: string
  }
}

export interface Uni30SwitchHistory {
  effectDate?: string;
  percentage?: number;
  destinationFund?: string;
  originFund?: string;
}

export interface Uni30EntitaPolizzaDatiFiscali {
	periodo?: string;
	tipoDatiFiscali?: string;
	/** @format double */
	importoLordo?: number;
	/** @format double */
	tasseVersate?: number;
}

export interface Uni30Trasferimento {
	effectDate?: string;
	state?: string;
	firstSubscriptionDate?: string;
	originRegistrationDate?: string;
	cedantFundExitDate?: string;
	covipSubscriptionNumber?: string;
	fundName?: string;
	totalAmount?: number;
}

export interface Uni30Quota {
	fundName?: string;
	fundCode?: string;
	shareNumber?: number;
	shareValue?: number;
	controvalore?: number;
	lastQuotation?: string;
	subscriberAmount?: number;
	employerAmount?: number;
	TRFamount?: number;
  referenceDate?: string
}

export interface Uni30Liquidazione {
	company?: string;
	practiceNumber?: number;
	standard?: number;
	requestDate?: string;
	liquidationType?: string;
	liquidationDescription?: string;
	state?: string;
	dematerialized?: number;
	receivedDate?: string;
	closureDate?: string;
}

export interface Uni30Titoli {
	total: number;
  currentYearTotal: number;
  lastYearTotal: number;
}

export interface Uni30Indirizzamento {
	fundCode?: string;
	fundDescription?: string;
	startDate?: string;
	endDate?: string;
	subscriberAmount?: number;
	employerAmount?: number;
	TFRamount?: number;
}


export interface Uni30Garanzia {
	garanzia?: string | null;
	capitalePremiPiano?: number | null;
	capitalePremiEstemp?: number | null;
	totaleCapitale?: number | null;
	premioAnnuo?: number | null;
	dataRiv?: string | null;
}

export interface Uni30Estemporaneo {
  companyCode: string;
  fundCode: string;
  percentage: number;
  positionNumber: number;
  progressiveTitleNumber: number;
  effectDate?: string;
}


export interface Uni30PremiumPaymentFurtherDetails {
	paymentMethod?: string;
	iban?: string;
	annualInstalment?: number;
	instalmentAmount?: number;
	lastPaymentDate?: string;
}

export interface Uni30PolicyValueFurtherDetails {
	financingScheme?: string;
	handleAgency?: string;
	debitAccount?: string
}

export interface Uni30PolicyValueFurtherDetails {
	financingScheme?: string;
	handleAgency?: string;
	debitAccount?: string
}

export interface Uni30Lettera {
	code?: string;
	description?: string;
	requestDate?: string;
	year?: string;
	beneficiary?: string;
	fileName?: string;
	letterId?: string;
}

export interface Uni30SummaryFurtherDetails {
	adesionType?: string;
	adesionDate?: string;
	subscriptionDate?: string;
}

export interface PolicyPersonUni20 {
	ateco: string;
	codiceFiscale: string;
	sae: string;
	indirizzoRecapito: AddressUni20Model;

	// Persona fisica
	nome?: string;
	cognome?: string;
	sesso?: string;
	professione?: string;
	datiNascita?: BirthDataUni20Model;
	datiResidenza?: AddressUni20Model;
	documentoRiconoscimento?: IdentityDocUni20;

	// Persona giuridica
	denominazione?: string;
	indirizzoSedeLegale?: AddressUni20Model;
	partitaIva?: string;
}

export interface IdentityDocUni20 {
    comuneRilascio: string;
    dataRilascio: string;
    enteRilascio: string;
    numeroDocumento: string;
    tipoDocumento: string;
}

export interface AddressUni20Model {
    cap: string;
    indirizzo: string;
    localita: string;
    provincia: string;
    stato: string;
}

export interface BirthDataUni20Model {
    capNascita: string;
    comuneNascita: string;
    dataNascita: string;
    nazioneNascita: string;
    provinciaNascita: string;
}

export interface IPersonIntesa {
  ateco: string;
  codiceFiscale: string;
  cognome: string;
  nome: string;
  professione: number;
  sae: string;
  sesso: string;
  gender?: string;
  datiNascita: IDatiNascitaIntesa;
  datiResidenza: IDatiIndirizzoIntesa;
  documentoRiconoscimento: IDatiDocumentoRiconoscimentoIntesa;
  indirizzoRecapito: IDatiIndirizzoIntesa;
}

export interface ILegalPersonIntesa {
  indirizzoRecapito: IDatiIndirizzoIntesa
  denominazione: string;
  indirizzoSedeLegale: IDatiIndirizzoIntesa;
  partitaIva: number;
  anagraficaRappresentanteLegale: IPersonIntesa;
}

export interface IDatiNascitaIntesa {
  capNascita: number;
  comuneNascita: string;
  dataNascita: string;
  nazioneNascita: string;
  provinciaNascita: string;
}

export interface IDatiIndirizzoIntesa {
  cap: number;
  indirizzo: string;
  localita: string;
  provincia: string;
  stato: string;
}

export interface IDatiDocumentoRiconoscimentoIntesa {
  comuneRilascio: string;
  dataRilascio: string;
  enteRilascio: string;
  numeroDocumento: string;
  tipoDocumento: string;
}

export interface IPolicyDocument {
  documentId: string;
  description: string;
  insertDate: any;
}

export interface IFondo {
  cFondo: string;
  descrizione: string
}

export interface IPolicyFunds {
  code: string;
  fundAmount: string;
  quoteShareNumber: string;
  quotesValue: string;
  investmentDate: string;
  description: string;
  lineName: string;
  percentage: string;
}

export interface IPolicyValuePayment {
  paymentAmount?: string;
  paymentDate?: string;
  paymentId?: string;
  paymentStatus?: string;
  showIcon?: any;
}

export interface IMovement {
  operationDescription: string;
  effectiveDate: string;
  emissionDate: string;
  movementStatus: string;
  benefitAtDate: string;
  movementId: string;
}

export interface IPolicyFilter {
  label: EPolicyTypes;
  originalStyle: string;
  currentStyle: string;
  policyTypes?: EPolicyTypes[];
}

export const DATE_FORMAT = 'YYYY-MM-DD';

export const DATE_FORMAT_SLASH = 'DD/MM/YYYY';

export const DATE_FORMAT_FROM_BE = 'DD-MM-YYYY';

export enum RegistryState {
  IN_VIGORE = 'In vigore',
  ANNULLATA = 'Annullata',
}

export enum RegistryType {
  POSTALE = 'Postale',
  FISCALE = 'Fiscale',
}

export interface IFigureAnagrafiche {
  c_nazione: string
  provincia: string
  cap: string
  indirizzo: string
  statoPolizzaPratica: RegistryState
  tipo_indirizzo: RegistryType
  n_indirizzo_alternativo: number;
}

export interface IPagatore {
  nome: string;
  cognome: string;
  codiceFiscale: string;
  dataNascita: string;
  naturaGiuridica?: string
  indirizzoNascita?: any
}

export interface IBeneficiarioPrevidenziale {
  nome: string;
  cognome: string;
  dataNascita: string;
  perc_Beneficio: string;
  codicebeneficiario: string;
  codiceTipoBeneficiario: string;
  anagrafico: number;
  codiceFiscale: string;
  tipoPersona: string;
  localitaNascita: string;
  provinciaNascita: string;
  nazioneNascita: string;
  localitaProvincia: string;
  relazione: string;
  relazioneDescrizione: string;
  via: string;
  nazione: string;
  provincia: string;
  indirizzo: string;
  sesso: string;
  ragioneSociale: string
  partitaIva: string
  cap: string
  localita: string;
}

export interface IPolicyAsset {
  fundCode: string;
  amount?: any;
  percentage?: number;
  attributeKey?: string;
  attributeDescription?: string;
  lineCode?: string;
  lineDescription?: string;
  fundAmount?: any;
  description?: string;
  futureFundAmount?: string;
  differenceAmount?: string;
  futureFundPercentage?: number;
  differencePercentage?: number;
  quoteValue?: string;
  quoteNumber?: string;
  quoteValueUpdateDate?: string;
  selected?: boolean;
  assuranceCode?: string;
  TFRAllocation?: string;
  subscriberAllocation?: string;
  companyAllocation?: string;
  pInvestment?: string;
}

export interface IInvestmentLine {
  code: string;
  description?: string;
  funds: IPolicyAsset[];
  selected?: boolean;
}

export interface ICountriesObject {
  countryISOCode: string;
  countryDescription: string;
  countryBDICode: string;
}

export interface IProvinceObject {
  provinceCode: string;
  provinceDescription: string;
}

export interface ICityObject {
  provinceCode: string;
  cityName: string;
  istatCode: string;
  exists?: boolean;
}

export const FONDO_GESTIONE_SEPARATA_LABEL: string =
  'Fondo a Gestione Separata';
